/**
 * FuturePage — full-page prediction timeline.
 *
 * Reuses existing endpoints only:
 *   • GET /api/vedic/topics          → per-topic upcoming windows
 *   • GET /api/kalki/companion       → Monish evidence (prediction_id,
 *                                       dasha, saturn phase) for the
 *                                       current turn's reasoning tag.
 *
 * No new backend. Predictions are grouped into Now / Next 3 months /
 * Next year / Next 3 years / Next 5 years / Long-term by their window
 * start date. All grade/planet/date values come straight from Monish
 * — never computed in the LLM.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/hooks/useAuth";
import KalkiGlobalHeader from "@/components/kalki/KalkiGlobalHeader";

const BUCKETS = [
  {id: "now",       label: "Now",             months: [-0.5, 0.1]},
  {id: "next3m",    label: "Next 3 months",   months: [0.1, 3]},
  {id: "next1y",    label: "Next year",       months: [3, 12]},
  {id: "next3y",    label: "Next 3 years",    months: [12, 36]},
  {id: "next5y",    label: "Next 5 years",    months: [36, 60]},
  {id: "longterm",  label: "Long-term",       months: [60, 9999]},
];

const GRADE_COLORS = {
  HIGH:   {bg: "rgba(93,0,255,0.12)",   fg: "#5D00FF"},
  MEDIUM: {bg: "rgba(255,46,154,0.10)", fg: "#FF2E9A"},
  LOW:    {bg: "rgba(0,0,0,0.06)",      fg: "#676773"},
};

const monthsFromNow = (iso) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return (d.getFullYear() - now.getFullYear()) * 12
       + (d.getMonth() - now.getMonth());
};

const fmtRange = (start, end) => {
  const opts = {month: "short", year: "numeric"};
  const s = start ? new Date(start).toLocaleDateString(undefined, opts) : "";
  const e = end   ? new Date(end).toLocaleDateString(undefined, opts) : "";
  if (s && e) return `${s} – ${e}`;
  return s || e || "";
};

export default function FuturePage() {
  useAuth();
  const navigate = useNavigate();
  const [topics, setTopics] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [companion, setCompanion] = useState(null);

  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const [t, c] = await Promise.allSettled([
          api.get("/vedic/topics"),
          api.get("/kalki/companion"),
        ]);
        if (!dead && t.status === "fulfilled") {
          const raw = t.value?.data?.topics;
          setTopics(raw && typeof raw === "object" ? raw : {});
        }
        if (!dead && c.status === "fulfilled") {
          setCompanion(c.value?.data?.companion || null);
        }
      } finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, []);

  const predictions = useMemo(() => {
    // Flatten topics → windows into a single array of {topic, grade,
    // start, end, one_liner, note, factor, prediction_id}.
    const out = [];
    Object.entries(topics || {}).forEach(([tname, tdata]) => {
      const ws = tdata?.windows || tdata?.next_5y || tdata?.upcoming || [];
      ws.forEach((w) => {
        const start = w.start || w.start_date;
        if (!start) return;
        out.push({
          topic: tname,
          grade: (w.grade || w.strength || "MEDIUM").toUpperCase(),
          start, end: w.end || w.end_date,
          one_liner: w.one_liner || w.note || w.summary || "",
          factor: w.factor || w.planet || "",
          prediction_id: w.prediction_id || null,
        });
      });
    });
    out.sort((a, b) => String(a.start).localeCompare(String(b.start)));
    return out;
  }, [topics]);

  const buckets = useMemo(() => {
    const result = BUCKETS.map((b) => ({...b, items: []}));
    predictions.forEach((p) => {
      const m = monthsFromNow(p.start);
      if (m === null) return;
      const bucket = result.find((b) => m >= b.months[0] && m < b.months[1]);
      if (bucket) bucket.items.push(p);
    });
    return result;
  }, [predictions]);

  const summary = useMemo(() => {
    const astro = companion?.astrology || {};
    const dash = astro?.dasha_now?.value || astro?.dasha_now || {};
    const md = dash?.mahadasha?.lord;
    const ad = dash?.antardasha?.lord;
    if (md && ad) {
      return `You are walking the ${md} major period with ${ad} sub-period. The windows below are what the Astro Monish engine sees ahead of you.`;
    }
    return "The windows below are what the Astro Monish engine sees ahead of you.";
  }, [companion]);

  const askKrishna = (p) => {
    const prompt = encodeURIComponent(
      `KALKI, tell me more about the ${p.topic} window from ${fmtRange(p.start, p.end)}.`
    );
    navigate(`/app/chat?prompt=${prompt}&source=future&topic=${encodeURIComponent(p.topic)}`);
  };

  const totalCount = predictions.length;

  return (
    <div data-testid="future-page"
         style={{minHeight: "100vh", background: "#FAFAFC"}}>
      <KalkiGlobalHeader />
      <main style={{maxWidth: 980, margin: "0 auto",
                     padding: "40px 20px 100px"}}>
        <div style={{textAlign: "center", marginBottom: 36}}>
          <div style={{fontSize: 10, letterSpacing: "0.32em",
                        color: "#5D00FF", marginBottom: 8}}>FUTURE</div>
          <h1 style={{fontSize: 36, fontWeight: 400, letterSpacing: "-0.01em",
                       color: "#1C1C1C", margin: "0 0 10px 0",
                       fontFamily: "'EB Garamond',serif"}}>
            Explore the periods ahead.
          </h1>
          <p data-testid="future-summary"
             style={{fontSize: 14, color: "#434353", maxWidth: 640,
                      marginInline: "auto", lineHeight: 1.6}}>
            {summary}
          </p>
        </div>

        {loading && (
          <p style={{textAlign: "center", color: "#9999A5", marginTop: 40}}>
            Loading your timeline…
          </p>
        )}

        {!loading && totalCount === 0 && (
          <div data-testid="future-empty"
               style={{textAlign: "center", padding: "60px 20px",
                        color: "#676773", maxWidth: 520,
                        marginInline: "auto", lineHeight: 1.7, fontSize: 14}}>
            Your Astro Monish engine is still warming up. Once your chart is
            fully computed, the periods ahead will appear here grouped by
            when they begin.
          </div>
        )}

        {!loading && totalCount > 0 && (
          <div data-testid="future-timeline"
               style={{display: "flex", flexDirection: "column", gap: 36,
                        marginTop: 20}}>
            {buckets.map((b) => {
              if (b.items.length === 0) return null;
              return (
                <section key={b.id} data-testid={`future-bucket-${b.id}`}
                         style={{position: "relative"}}>
                  <div style={{display: "flex", alignItems: "baseline",
                                justifyContent: "space-between",
                                marginBottom: 14}}>
                    <h2 style={{fontSize: 11, letterSpacing: "0.32em",
                                 color: "#9999A5", margin: 0,
                                 fontWeight: 500}}>
                      {b.label.toUpperCase()}
                    </h2>
                    <span style={{fontSize: 11, color: "#A1A1AA"}}>
                      {b.items.length} window{b.items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div style={{display: "flex", flexDirection: "column",
                                gap: 12}}>
                    {b.items.map((p, i) => (
                      <PredictionCard key={`${p.topic}-${p.start}-${i}`}
                        p={p}
                        expanded={expanded[`${p.topic}-${p.start}`]}
                        onToggle={() => setExpanded((x) => ({
                          ...x,
                          [`${p.topic}-${p.start}`]:
                            !x[`${p.topic}-${p.start}`]}))}
                        onAsk={() => askKrishna(p)} />
                    ))}
                  </div>
                </section>
              );
            })}
            {buckets.every((b) => b.items.length === 0) && !loading && (
              <div style={{textAlign: "center", color: "#9999A5",
                            fontSize: 13.5, padding: 40}}>
                No upcoming windows surfaced in the available horizon.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function PredictionCard({ p, expanded, onToggle, onAsk }) {
  const gc = GRADE_COLORS[p.grade] || GRADE_COLORS.MEDIUM;
  return (
    <div data-testid={`future-card-${p.topic}-${p.start}`}
         className="kalki-snippet kalki-snippet--astro"
         style={{borderRadius: 18,
                  boxShadow: "0 4px 16px rgba(20,10,60,0.04)",
                  padding: 20}}>
      <div style={{display: "flex", alignItems: "center",
                    justifyContent: "space-between", marginBottom: 8,
                    gap: 10, flexWrap: "wrap"}}>
        <div style={{fontSize: 10, letterSpacing: "0.3em",
                      color: "#5D00FF", textTransform: "uppercase"}}>
          {p.topic}
        </div>
        <div style={{display: "flex", gap: 10, alignItems: "center"}}>
          <span data-testid={`future-grade-${p.grade}`}
                style={{fontSize: 10, letterSpacing: "0.2em",
                         padding: "4px 10px", borderRadius: 999,
                         background: gc.bg, color: gc.fg,
                         fontWeight: 500}}>{p.grade}</span>
          <span style={{fontSize: 12, color: "#676773"}}>
            {fmtRange(p.start, p.end)}
          </span>
        </div>
      </div>
      <div style={{fontSize: 15.5, color: "#1C1C1C",
                    fontFamily: "'EB Garamond',serif", lineHeight: 1.4,
                    marginBottom: expanded ? 10 : 0}}>
        {p.one_liner || ""}
      </div>
      {expanded && (
        <div data-testid={`future-details-${p.topic}-${p.start}`}
             style={{fontSize: 13, color: "#676773", lineHeight: 1.6,
                      marginTop: 6, padding: "12px 14px", borderRadius: 12,
                      background: "rgba(93,0,255,0.03)",
                      border: "1px solid rgba(93,0,255,0.08)"}}>
          {p.factor && <div><strong>Planetary factor:</strong> {p.factor}</div>}
          {p.prediction_id && <div style={{fontSize: 10,
              letterSpacing: "0.14em", color: "#A1A1AA", marginTop: 6,
              textTransform: "uppercase"}}>
            Monish ref · {p.prediction_id}
          </div>}
          <div style={{fontSize: 11, color: "#A1A1AA", fontStyle: "italic",
                        marginTop: 8}}>
            A possibility · not a certainty. Timing guidance only — not
            financial or legal advice.
          </div>
        </div>
      )}
      <div style={{display: "flex", gap: 10, marginTop: 12,
                    alignItems: "center"}}>
        <button data-testid={`future-toggle-${p.topic}-${p.start}`}
                onClick={onToggle}
                style={{background: "none", border: 0, color: "#5D00FF",
                         fontSize: 11, letterSpacing: "0.15em",
                         cursor: "pointer", padding: 0,
                         display: "inline-flex", alignItems: "center",
                         gap: 4}}>
          {expanded ? <><ChevronUp size={12}/> HIDE</>
                    : <><ChevronDown size={12}/> DETAILS</>}
        </button>
        <button data-testid={`future-ask-${p.topic}-${p.start}`}
                onClick={onAsk}
                style={{marginLeft: "auto",
                         background: "linear-gradient(135deg,#5D00FF,#FF2E9A)",
                         color: "white", border: 0, padding: "8px 16px",
                         borderRadius: 999, fontSize: 11,
                         letterSpacing: "0.14em", cursor: "pointer"}}>
          ASK KALKI
        </button>
      </div>
    </div>
  );
}
